<template>
	<div class="modal-card unlock-card">
		<header class="modal-card-head unlock-head">
			<div>
				<p class="unlock-eyebrow">Plan de cuentas</p>
				<h3 class="unlock-title">Quitar el bloqueo</h3>
			</div>
			<button type="button" class="delete" aria-label="Cerrar" @click="$emit('close')"></button>
		</header>

		<section class="modal-card-body unlock-body">
			<p class="unlock-intro">
				Para quitar el bloqueo escribe tu correo, copia el código de
				confirmación y explica el motivo. El desbloqueo queda registrado.
			</p>

			<div class="unlock-section">
				<p class="unlock-label">Código de confirmación</p>
				<div class="unlock-code" aria-live="polite">
					<span v-if="loadingChallenge" class="unlock-code__text unlock-code__text--muted">
						Generando…
					</span>
					<span v-else-if="challengeCode" class="unlock-code__text">{{ challengeCode }}</span>
					<span v-else class="unlock-code__text unlock-code__text--muted">—</span>
					<button type="button" class="unlock-link" :disabled="loadingChallenge"
						@click="requestChallenge">
						Generar otro código
					</button>
				</div>
				<p v-if="challengeCode" class="unlock-hint">
					Vale {{ expiresInMinutes }} minutos y se usa una sola vez.
				</p>
			</div>

			<div class="unlock-section">
				<label class="unlock-label" for="unlock-email">Tu correo</label>
				<input id="unlock-email" v-model.trim="email" class="input unlock-input" type="email"
					autocomplete="email" placeholder="nombre@cforemoto.com" />
			</div>

			<div class="unlock-section">
				<label class="unlock-label" for="unlock-code">Código</label>
				<input id="unlock-code" v-model.trim="code" class="input unlock-input unlock-input--code"
					type="text" autocomplete="off" maxlength="6" placeholder="6 caracteres" />
			</div>

			<div class="unlock-section">
				<label class="unlock-label" for="unlock-reason">Motivo</label>
				<textarea id="unlock-reason" v-model.trim="reason" class="textarea unlock-input" rows="3"
					placeholder="Por qué hay que modificar el plan de cuentas (mínimo 10 caracteres)"></textarea>
			</div>

			<p v-if="errorMessage" class="unlock-error" role="alert">{{ errorMessage }}</p>
		</section>

		<footer class="modal-card-foot unlock-foot">
			<p class="unlock-note">Solo el analista asignado a la empresa puede quitar el bloqueo.</p>
			<div class="unlock-foot__actions">
				<button type="button" class="button unlock-btn unlock-btn--ghost" @click="$emit('close')">
					Volver
				</button>
				<button type="button" class="button unlock-btn unlock-btn--primary"
					:disabled="!canSubmit" @click="submit">
					{{ submitting ? "Quitando…" : "Quitar bloqueo" }}
				</button>
			</div>
		</footer>
	</div>
</template>

<script>
import { mapActions } from "vuex";
import {
	consoleHandlerError,
	extractErrorMessage,
	notificate,
} from "@/utils/message";

export const REASON_MIN_LENGTH = 10;
export const CODE_LENGTH = 6;

export default {
	name: "UnlockAccountPlanModal",
	props: {
		companyId: {
			type: Number,
			required: true,
		},
	},
	data() {
		return {
			challengeCode: "",
			expiresIn: 0,
			loadingChallenge: false,
			email: "",
			code: "",
			reason: "",
			submitting: false,
			errorMessage: "",
		};
	},
	computed: {
		expiresInMinutes() {
			return Math.max(1, Math.round(this.expiresIn / 60));
		},
		canSubmit() {
			return (
				!this.submitting &&
				!!this.challengeCode &&
				this.email.length > 0 &&
				this.code.length === CODE_LENGTH &&
				this.reason.length >= REASON_MIN_LENGTH
			);
		},
	},
	created() {
		this.requestChallenge();
	},
	methods: {
		...mapActions("analystCompany", [
			"fetchUnlockChallenge",
			"unlockAccountPlan",
		]),
		async requestChallenge() {
			this.loadingChallenge = true;
			this.errorMessage = "";
			try {
				const data = await this.fetchUnlockChallenge(this.companyId);
				this.challengeCode = data.code;
				this.expiresIn = data.expires_in;
			} catch (error) {
				// 403 si no es analista asignado: el modal lo dice tal cual.
				this.challengeCode = "";
				this.errorMessage = extractErrorMessage(
					error, "No se pudo generar el código. Vuelve a intentarlo.",
				);
				consoleHandlerError({ error, silent: true });
			} finally {
				this.loadingChallenge = false;
			}
		},
		async submit() {
			if (!this.canSubmit) return;
			this.submitting = true;
			this.errorMessage = "";
			try {
				await this.unlockAccountPlan({
					companyId: this.companyId,
					email: this.email,
					code: this.code.toUpperCase(),
					reason: this.reason,
				});
				notificate("Bloqueo quitado. El plan de cuentas vuelve a ser editable.", "is-success is-light");
				this.$emit("unlocked");
				this.$emit("close");
			} catch (error) {
				// 403 (correo/código, intentos agotados, expirado) o 400 (motivo):
				// el backend ya dice qué pasó y qué hacer.
				this.errorMessage = extractErrorMessage(
					error, "No se pudo quitar el bloqueo. Vuelve a intentarlo.",
				);
				consoleHandlerError({ error, silent: true });
			} finally {
				this.submitting = false;
			}
		},
	},
};
</script>

<style scoped lang="scss">
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss).
.unlock-card {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	width: min(520px, 94vw);
	display: flex;
	flex-direction: column;
	max-height: 86vh;
	background-color: var(--ap-paper);
	border-radius: var(--ap-radius);
	overflow: hidden;
}

.unlock-head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	background-color: var(--ap-paper);
	border-bottom: 1px solid var(--ap-line);
}

.unlock-eyebrow {
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.unlock-title {
	font-size: 20px;
	font-weight: 700;
	letter-spacing: -0.01em;
	color: var(--ap-ink);
}

.unlock-body {
	background-color: var(--ap-paper);
}

.unlock-intro {
	font-size: 14px;
	line-height: 20px;
	color: var(--ap-slate);
	margin-bottom: 1rem;
}

.unlock-section {
	padding: 0.75rem 0;
	border-top: 1px solid var(--ap-line);
}

.unlock-label {
	display: block;
	margin-bottom: 0.35rem;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.unlock-code {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;

	&__text {
		font-size: 28px;
		font-weight: 700;
		letter-spacing: 0.18em;
		font-variant-numeric: tabular-nums;
		color: var(--ap-ink);

		&--muted {
			font-size: 16px;
			letter-spacing: 0;
			font-weight: 500;
			color: var(--ap-muted);
		}
	}
}

.unlock-hint {
	margin-top: 0.25rem;
	font-size: 12px;
	color: var(--ap-muted);
}

.unlock-link {
	background: none;
	border: 0;
	padding: 0;
	font: inherit;
	font-size: 13px;
	font-weight: 600;
	color: var(--ap-teal);
	cursor: pointer;

	&:hover {
		color: var(--ap-teal-deep);
	}

	&:disabled {
		color: var(--ap-muted);
		cursor: wait;
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

.unlock-input {
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	color: var(--ap-ink);
	box-shadow: none;
	transition: border-color 0.14s var(--ap-ease), box-shadow 0.14s var(--ap-ease);

	&:focus,
	&:focus-visible {
		border-color: var(--ap-teal);
		box-shadow: 0 0 0 3px var(--ap-teal-wash);
	}

	&--code {
		text-transform: uppercase;
		letter-spacing: 0.18em;
		font-variant-numeric: tabular-nums;
	}
}

.unlock-error {
	margin-top: 0.75rem;
	padding: 0.5rem 0.75rem;
	border: 1px solid var(--ap-alert);
	border-radius: var(--ap-radius-sm);
	background: var(--ap-alert-wash);
	color: var(--ap-alert);
	font-size: 13px;
	line-height: 20px;
}

.unlock-foot {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	background-color: var(--ap-paper);
	border-top: 1px solid var(--ap-line);

	&__actions {
		display: flex;
		gap: 0.5rem;
	}
}

.unlock-note {
	font-size: 12px;
	color: var(--ap-muted);
}

.unlock-btn {
	font-family: var(--ap-font);
	font-weight: 600;
	border-radius: var(--ap-radius-pill);

	&--ghost {
		background: var(--ap-paper);
		border-color: var(--ap-line-strong);
		color: var(--ap-ink);
	}

	&--primary {
		background-color: var(--ap-ink);
		border-color: transparent;
		color: var(--ap-paper);

		&:hover:not(:disabled) {
			background-color: var(--ap-teal-deep);
		}
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

@media (prefers-reduced-motion: reduce) {
	.unlock-input {
		transition: none;
	}
}
</style>
